import { pink } from '@mui/material/colors';
import { alpha, createTheme } from '@mui/material/styles';

// Brand colors — derived from the WebDrvn logo.
//   Navy   #052372  (primary, atmospheric field)
//   Orange #FA871C  (secondary, ignition accent)
//
// Each is exposed as a full MUI Color object so existing consumers
// (e.g. `secondaryColor[500]`) keep working. The brand value sits at [500]
// so MUI's automatic main/light/dark derivation lands on the brand color.

export const primaryColor = {
  50: '#e7eaf3',
  100: '#bdc4dc',
  200: '#909bc3',
  300: '#6371aa',
  400: '#3f5197',
  500: '#052372', // brand navy
  600: '#041e6a',
  700: '#03175e',
  800: '#031150',
  900: '#020a3a',
  A100: '#9aa1d2',
  A200: '#5a66b5',
  A400: '#1a2c9c',
  A700: '#03175e',
  contrastDefaultColor: 'light',
} as const;

export const secondaryColor = {
  50: '#fff3e6',
  100: '#ffdfbf',
  200: '#ffcb95',
  300: '#fdb469',
  400: '#fca345',
  500: '#FA871C', // brand orange
  600: '#e07408',
  700: '#bd5f06',
  800: '#984a04',
  900: '#723702',
  A100: '#ffdfbf',
  A200: '#ffcb95',
  A400: '#fca345',
  A700: '#e07408',
  contrastDefaultColor: 'dark',
} as const;

export const errorColor = pink;

const fontFamily = [
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  'system-ui',
  'sans-serif',
].join(',');

const displayFamily = ['"Inter Tight"', 'Inter', 'sans-serif'].join(',');

const monoFamily = [
  '"JetBrains Mono"',
  '"SFMono-Regular"',
  'Menlo',
  'Consolas',
  'monospace',
].join(',');

const sharedTypography = {
  fontFamily,
  h1: { fontFamily: displayFamily, fontWeight: 700, letterSpacing: '-0.022em', lineHeight: 1.05 },
  h2: { fontFamily: displayFamily, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 },
  h3: { fontFamily: displayFamily, fontWeight: 700, letterSpacing: '-0.018em', lineHeight: 1.15 },
  h4: { fontFamily: displayFamily, fontWeight: 700, letterSpacing: '-0.014em', lineHeight: 1.2 },
  h5: { fontFamily: displayFamily, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.25 },
  h6: { fontFamily: displayFamily, fontWeight: 600, letterSpacing: '-0.005em', lineHeight: 1.3 },
  subtitle1: { fontWeight: 500, letterSpacing: '-0.005em' },
  subtitle2: { fontWeight: 600, letterSpacing: '0.01em' },
  body1: { letterSpacing: '-0.003em' },
  body2: { letterSpacing: '-0.002em' },
  button: { textTransform: 'none' as const, fontWeight: 600, letterSpacing: '-0.005em' },
  overline: {
    fontFamily: monoFamily,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    lineHeight: 1.6,
  },
  caption: { letterSpacing: '0.005em' },
};

export const defaultTheme = createTheme({
  palette: {
    mode: 'light',
    primary: primaryColor,
    secondary: secondaryColor,
    error: errorColor,
    background: {
      default: '#f3f5fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#0b1230',
      secondary: '#4a5377',
      disabled: '#a8aec5',
    },
    divider: 'rgba(11, 18, 48, 0.08)',
  },
  shape: {
    borderRadius: 12,
  },
  typography: sharedTypography,
  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
          border: '1px solid rgba(11, 18, 48, 0.06)',
          boxShadow: '0 1px 2px rgba(11, 18, 48, 0.04), 0 8px 24px -12px rgba(11, 18, 48, 0.10)',
        },
        elevation0: {
          boxShadow: 'none',
          border: '1px solid rgba(11, 18, 48, 0.06)',
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(11, 18, 48, 0.06)',
          boxShadow: '0 1px 2px rgba(11, 18, 48, 0.04), 0 12px 32px -16px rgba(5, 35, 114, 0.18)',
          transition: 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms cubic-bezier(0.16, 1, 0.3, 1), border-color 220ms ease',
          overflow: 'hidden',
          '&:hover': {
            borderColor: 'rgba(5, 35, 114, 0.20)',
            boxShadow: '0 1px 2px rgba(11, 18, 48, 0.06), 0 24px 48px -16px rgba(5, 35, 114, 0.24)',
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          transition: 'background-color 220ms ease',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 16,
          paddingBlock: 8,
          fontWeight: 600,
          letterSpacing: '-0.005em',
          transition: 'transform 160ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms ease, background-color 200ms ease, color 200ms ease',
          '&:active': {
            transform: 'translateY(1px) scale(0.997)',
          },
        },
        sizeLarge: {
          paddingInline: 20,
          paddingBlock: 11,
          fontSize: 15,
        },
        sizeSmall: {
          paddingInline: 10,
          paddingBlock: 5,
          fontSize: 13,
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: ({ theme }) => ({
            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 1)} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 1px 0 ${alpha('#ffffff', 0.12)} inset, 0 8px 22px -10px ${alpha(theme.palette.primary.main, 0.6)}`,
            color: '#ffffff',
            '&:hover': {
              background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 1)} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 1px 0 ${alpha('#ffffff', 0.16)} inset, 0 12px 28px -10px ${alpha(theme.palette.primary.main, 0.7)}`,
            },
            // Without this override the gradient `background:` shorthand
            // wins over MUI's default Mui-disabled `backgroundColor`, leaving
            // dark grey disabled text on the dark navy gradient (unreadable).
            '&.Mui-disabled': {
              background: 'rgba(11, 18, 48, 0.08)',
              color: 'rgba(11, 18, 48, 0.36)',
              boxShadow: 'none',
            },
          }),
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: ({ theme }) => ({
            background: `linear-gradient(180deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: '#1a0f00',
            boxShadow: `0 1px 0 ${alpha('#ffffff', 0.18)} inset, 0 10px 24px -10px ${alpha(theme.palette.secondary.main, 0.55)}`,
            '&:hover': {
              boxShadow: `0 1px 0 ${alpha('#ffffff', 0.22)} inset, 0 14px 30px -10px ${alpha(theme.palette.secondary.main, 0.65)}`,
            },
            '&.Mui-disabled': {
              background: 'rgba(11, 18, 48, 0.08)',
              color: 'rgba(11, 18, 48, 0.36)',
              boxShadow: 'none',
            },
          }),
        },
        {
          props: { variant: 'outlined' },
          style: ({ theme }) => ({
            borderColor: alpha(theme.palette.primary.main, 0.24),
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.46),
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            },
          }),
        },
        {
          props: { variant: 'text' },
          style: ({ theme }) => ({
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            },
          }),
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'background-color 180ms ease, color 180ms ease',
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          backgroundColor: '#ffffff',
          transition: 'box-shadow 200ms ease, border-color 200ms ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(11, 18, 48, 0.14)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(11, 18, 48, 0.28)',
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 1.5,
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          color: 'rgba(11, 18, 48, 0.62)',
          '&.Mui-focused': {
            color: '#052372',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 12,
          letterSpacing: '-0.002em',
          color: 'rgba(11, 18, 48, 0.5)',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(11, 18, 48, 0.62)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          letterSpacing: '-0.003em',
        },
        outlined: {
          borderColor: 'rgba(11, 18, 48, 0.14)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(3, 17, 80, 0.94)',
          color: '#ffffff',
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '-0.002em',
          padding: '6px 10px',
          borderRadius: 8,
          boxShadow: '0 8px 24px -10px rgba(3, 17, 80, 0.5)',
        },
        arrow: {
          color: 'rgba(3, 17, 80, 0.94)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(11, 18, 48, 0.08)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          marginInline: theme.spacing(1.25),
          marginBottom: 2,
          paddingTop: 7,
          paddingBottom: 7,
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(1.5),
          transition: 'background-color 200ms ease, color 200ms ease, transform 200ms ease',
          '& .MuiListItemText-primary': {
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: '-0.003em',
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.10),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.14),
            },
            '& .MuiListItemText-primary': {
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          },
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 14,
          letterSpacing: '-0.003em',
        },
        secondary: {
          fontSize: 12,
          letterSpacing: '-0.002em',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          letterSpacing: '-0.003em',
          minHeight: 44,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid rgba(11, 18, 48, 0.08)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 30px 80px -30px rgba(5, 35, 114, 0.55), 0 1px 2px rgba(11, 18, 48, 0.06)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          marginTop: 6,
          border: '1px solid rgba(11, 18, 48, 0.08)',
          boxShadow: '0 18px 40px -16px rgba(5, 35, 114, 0.28), 0 1px 2px rgba(11, 18, 48, 0.06)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          marginInline: 6,
          fontSize: 14,
          fontWeight: 500,
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.10),
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontWeight: 500,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          letterSpacing: '-0.005em',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(3, 17, 80, 0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: primaryColor,
    secondary: secondaryColor,
    error: errorColor,
    background: {
      default: '#03124a',
      paper: '#04154f',
    },
    text: {
      primary: '#f4f6ff',
      secondary: 'rgba(244, 246, 255, 0.72)',
      disabled: 'rgba(244, 246, 255, 0.42)',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  shape: {
    borderRadius: 12,
  },
  typography: sharedTypography,
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          letterSpacing: '-0.005em',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.18)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.36)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(244, 246, 255, 0.65)',
        },
      },
    },
  },
});
